import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { workoutAPI } from '../../services/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronDownIcon, ChevronUpIcon, PlayIcon, ClipboardDocumentListIcon, ClockIcon } from '@heroicons/react/24/outline';
import WorkoutLogSession from './components/WorkoutLogSession';
import WorkoutHistory from './components/WorkoutHistory';
import type { ActiveExercise } from '../../types';

function safeSessionSet(key: string, value: string) {
  try { sessionStorage.setItem(key, value); } catch { /* noop */ }
}

type Tab = 'plan' | 'log' | 'history';

function PlanTab() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const [plan, setPlan] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [openDays, setOpenDays] = useState<Set<number>>(new Set([0]));

  useEffect(() => { loadPlan(); }, []);

  const loadPlan = async () => {
    try {
      setLoading(true);
      const result = await workoutAPI.getWorkoutPlans() as { success?: boolean; plans?: any[] };
      if (result?.success && result?.plans?.length) {
        setPlan(result.plans[0]);
      }
    } catch (err: any) {
      console.error('Load workout plan error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!user?.weight || !user?.height || !user?.age) {
      setError(t('workoutPlans.missingUserData'));
      return;
    }
    setGenerating(true);
    setError('');
    try {
      const result = await workoutAPI.generateWorkoutPlan() as { success?: boolean; plan?: any; message?: string };
      if (result?.success && result?.plan) {
        setPlan(result.plan);
        setOpenDays(new Set([0]));
      } else {
        setError(result?.message || 'Failed to generate plan');
      }
    } catch (err: any) {
      setError(err.message || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const toggleDay = (idx: number) => {
    setOpenDays(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const startWorkout = (dayIdx: number, day: any) => {
    const exercises: ActiveExercise[] = (day.exercises || []).map((ex: any, i: number) => ({
      id: `${dayIdx}-${i}`,
      name: ex.name,
      plannedSets: Number(ex.sets) || 3,
      plannedReps: ex.reps || '8-12',
      sets: Array.from({ length: Number(ex.sets) || 3 }, () => ({ reps: 0, weight: 0, completed: false })),
    }));

    const draft = {
      startedAt: new Date().toISOString(),
      name: day.day || day.focus || `Day ${dayIdx + 1}`,
      planDayIndex: dayIdx,
      exercises,
    };

    safeSessionSet('activeWorkoutDraft', JSON.stringify(draft));
    setSearchParams({ tab: 'log' });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="max-w-2xl mx-auto">
        {error && <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4">{error}</div>}
        {(!user?.weight || !user?.height || !user?.age) ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
            <p className="text-yellow-800 mb-4">{t('workoutPlans.missingUserData')}</p>
            <button onClick={() => navigate('/')} className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
              {t('workoutPlans.manageUserData')}
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <div className="text-5xl mb-4">💪</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">No workout plan yet</h3>
            <p className="text-gray-500 mb-6">{t('workoutPlans.noPlans')}</p>
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="px-8 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50"
            >
              {generating ? t('workoutPlans.generating') : t('workoutPlans.generateButton')}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">{plan.title}</h2>
          {plan.description && <p className="text-sm text-gray-500 mt-0.5">{plan.description}</p>}
        </div>
        <div className="flex items-center gap-2">
          {plan.level && (
            <span className="px-2.5 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium capitalize">{plan.level}</span>
          )}
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {generating ? 'Generating...' : t('workoutPlans.generateNew')}
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4">{error}</div>}

      <div className="space-y-3">
        {plan.schedule?.map((day: any, idx: number) => {
          const isOpen = openDays.has(idx);
          const exerciseCount = day.exercises?.length || 0;
          return (
            <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleDay(idx)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-indigo-700 font-bold text-sm">{idx + 1}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{day.day || `Day ${idx + 1}`}</p>
                    {day.focus && <p className="text-sm text-gray-500">{day.focus}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-3 ml-4">
                  <span className="text-xs text-gray-400 hidden sm:block">{exerciseCount} exercises</span>
                  {isOpen ? <ChevronUpIcon className="w-4 h-4 text-gray-400" /> : <ChevronDownIcon className="w-4 h-4 text-gray-400" />}
                </div>
              </div>

              {isOpen && (
                <div className="border-t border-gray-100 px-4 pb-4 pt-3">
                  <div className="space-y-2 mb-4">
                    {day.exercises?.map((ex: any, exIdx: number) => (
                      <div key={exIdx} className="flex items-start justify-between py-2 border-b border-gray-50 last:border-0">
                        <div className="flex-1 min-w-0">
                          <a
                            href={`https://www.youtube.com/results?search_query=${encodeURIComponent(ex.name + ' exercise form')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-indigo-600 hover:underline font-medium text-sm"
                            onClick={e => e.stopPropagation()}
                          >
                            {ex.name}
                          </a>
                          {ex.description && <p className="text-xs text-gray-400 mt-0.5">{ex.description}</p>}
                        </div>
                        <div className="flex items-center gap-3 ml-4 text-sm text-gray-600 flex-shrink-0">
                          <span className="bg-gray-100 px-2 py-0.5 rounded text-xs font-medium">{ex.sets}×{ex.reps}</span>
                          {ex.rest && <span className="text-xs text-gray-400">{ex.rest}</span>}
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={(e) => { e.stopPropagation(); startWorkout(idx, day); }}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
                  >
                    <PlayIcon className="w-4 h-4" />
                    Start This Workout
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-5 bg-blue-50 rounded-xl p-4">
        <p className="text-blue-800 text-sm">
          {t('workoutPlans.modifyPlan')}{' '}
          <button onClick={() => navigate('/ai-assistant')} className="font-semibold underline">
            {t('workoutPlans.goAIAssistant')}
          </button>
        </p>
      </div>
    </div>
  );
}

const tabs: { key: Tab; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { key: 'plan', label: 'Plan', icon: ClipboardDocumentListIcon },
  { key: 'log', label: 'Log Workout', icon: PlayIcon },
  { key: 'history', label: 'History', icon: ClockIcon },
];

export default function WorkoutPlans() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as Tab) || 'plan';

  const setTab = (tab: Tab) => setSearchParams({ tab });

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-2xl font-bold text-gray-900">{t('workoutPlans.title')}</h1>
      </div>

      {/* Sub-tab navigation */}
      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl w-fit">
        {tabs.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === key
                ? 'bg-white text-indigo-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'plan' && <PlanTab />}
      {activeTab === 'log' && <WorkoutLogSession />}
      {activeTab === 'history' && <WorkoutHistory />}
    </div>
  );
}
